import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import RoleDropdown from "./RoleDropdown";
import UserAlreadyExistsDialog from "./UserAlreadyExistsDialog";
import {
  getContactLookupErrorMessage,
  getUserByContact,
  isUserMappedToOutlet,
} from "./addUserApi";
import {
  getCachedOutletUsers,
  getOutletUsers,
  invalidateOutletUsersCache,
  setCachedOutletUsers,
} from "./manageUsersApi";
import { getSelectedOutletId } from "./outletStorage";
import {
  createUserAndMapToOutlet,
  getUserMappingErrorMessage,
  isDuplicateUserMappingError,
  normalizeMappingPhone,
} from "./userMappingApi";
import {
  isAddUserFormValid,
  isValidEmail,
  isValidPhoneNumber,
} from "./userValidation";
import { normalizeUserRole } from "./userRoles";
import {
  ActionButton,
  AddUserForm,
  Content,
  FieldMessage,
  FormCard,
  Header,
  HeaderTitle,
  LookupMessage,
  Page,
  PageShell,
  SectionTitle,
  SubmitArea,
  SubmitButton,
  TextInput,
} from "./styles.addUser";

const EMPTY_FORM = {
  phone: "",
  email: "",
  name: "",
  role: "",
};

const LOOKUP_DELAY_MS = 500;

const getNormalizedLookupValue = (field, value) =>
  field === "phone"
    ? String(value).replace(/\D/g, "").slice(0, 10)
    : String(value).trim().toLowerCase();

const isValidLookupValue = (field, value) =>
  field === "phone" ? isValidPhoneNumber(value) : isValidEmail(value);

const isTransientLookupError = (error) => {
  const status = error?.response?.status;

  return (
    !status ||
    status === 408 ||
    status === 429 ||
    status >= 500
  );
};

const AddUser = () => {
  const navigate = useNavigate();
  const outletId = getSelectedOutletId();
  const lookupSequenceRef = useRef(0);
  const lastLookupRef = useRef("");
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [activeContactField, setActiveContactField] = useState("");
  const [lookupSource, setLookupSource] = useState("");
  const [lookupStatus, setLookupStatus] = useState("idle");
  const [existingUser, setExistingUser] = useState(null);
  const [isMappedToOutlet, setIsMappedToOutlet] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [submissionError, setSubmissionError] = useState("");
  const [showExistingUserDialog, setShowExistingUserDialog] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const performContactLookup = useCallback(
    async (field, rawValue) => {
      const value = getNormalizedLookupValue(field, rawValue);
      if (!isValidLookupValue(field, value)) return;

      const lookupKey = `${field}:${value}`;
      if (lastLookupRef.current === lookupKey) return;
      lastLookupRef.current = lookupKey;

      const lookupSequence = ++lookupSequenceRef.current;
      setLookupStatus("checking");
      setLookupError("");
      setSubmissionError("");

      try {
        const user = await getUserByContact(value);
        if (lookupSequence !== lookupSequenceRef.current) return;

        if (!user) {
          setExistingUser(null);
          setLookupSource("");
          setIsMappedToOutlet(false);
          setLookupStatus("not-found");
          return;
        }

        // The mapped-to-outlet gate must not depend on the list having been
        // visited first: on a cold entry (deep-link, refresh, post-login, or
        // right after an add invalidates it) the cache is empty. Fetch it once
        // so an already-mapped user is reliably detected instead of relying on
        // the backend duplicate error at submit time.
        let outletUsers = getCachedOutletUsers(outletId);
        if (!outletUsers) {
          try {
            outletUsers = await getOutletUsers(outletId);
            if (lookupSequence !== lookupSequenceRef.current) return;
            setCachedOutletUsers(outletId, outletUsers);
          } catch (outletUsersError) {
            outletUsers = [];
          }
        }
        const mappedToOutlet = isUserMappedToOutlet(user, outletUsers);
        const mappedOutletUser = mappedToOutlet
          ? outletUsers.find(
              (outletUser) => String(outletUser.id) === String(user.id)
            )
          : null;
        const mappedOutletRole = normalizeUserRole(
          mappedOutletUser?.sellerOutlets?.find(
            (sellerOutlet) =>
              String(sellerOutlet.outlet_id) === String(outletId)
          )?.role
        );
        const backendPhone = normalizeMappingPhone(user.phone);
        const backendName =
          user.name === "Unnamed user" ? "" : String(user.name || "").trim();

        setFormValues((currentValues) => ({
          phone: backendPhone || (field === "phone" ? value : ""),
          email:
            String(user.email || "").trim().toLowerCase() ||
            (field === "email" ? value : ""),
          name: backendName,
          role: mappedToOutlet ? mappedOutletRole : currentValues.role,
        }));
        setExistingUser(user);
        setLookupSource(field);
        setIsMappedToOutlet(mappedToOutlet);
        setLookupStatus("found");
        setShowExistingUserDialog(true);
      } catch (error) {
        if (lookupSequence !== lookupSequenceRef.current) return;
        lastLookupRef.current = "";

        if (isTransientLookupError(error)) {
          // Contact lookup only powers autofill. The create/mapping endpoint
          // resolves the contact again, so a temporary lookup outage should
          // not leave a technical error on screen or block a valid form.
          setExistingUser(null);
          setLookupSource("");
          setIsMappedToOutlet(false);
          setLookupStatus("unavailable");
          setLookupError("");
          return;
        }

        setLookupStatus("error");
        setLookupError(getContactLookupErrorMessage(error));
      }
    },
    [outletId]
  );

  useEffect(() => {
    if (!activeContactField) return undefined;

    const value = getNormalizedLookupValue(
      activeContactField,
      formValues[activeContactField]
    );
    if (!isValidLookupValue(activeContactField, value)) return undefined;

    const timer = window.setTimeout(
      () => performContactLookup(activeContactField, value),
      LOOKUP_DELAY_MS
    );

    return () => window.clearTimeout(timer);
  }, [
    activeContactField,
    formValues.email,
    formValues.phone,
    performContactLookup,
  ]);

  const handleContactChange = (field) => (event) => {
    const value =
      field === "phone"
        ? event.target.value.replace(/\D/g, "").slice(0, 10)
        : event.target.value;

    lookupSequenceRef.current += 1;
    lastLookupRef.current = "";
    setActiveContactField(field);
    setLookupStatus("idle");
    setLookupError("");
    setSubmissionError("");
    setShowExistingUserDialog(false);
    setIsMappedToOutlet(false);

    setFormValues((currentValues) => {
      if (existingUser) {
        return {
          ...EMPTY_FORM,
          role: isMappedToOutlet ? "" : currentValues.role,
          [field]: value,
        };
      }

      return {
        ...currentValues,
        [field]: value,
      };
    });
    setExistingUser(null);
    setLookupSource("");
  };

  const handleContactBlur = (field) => () => {
    if (existingUser && lookupSource !== field) return;

    setTouchedFields((currentFields) => ({
      ...currentFields,
      [field]: true,
    }));
    performContactLookup(field, formValues[field]);
  };

  const updateField = (field) => (event) => {
    setFormValues((currentValues) => ({
      ...currentValues,
      [field]: event.target.value,
    }));
    setSubmissionError("");
  };

  const isPhoneLocked = Boolean(existingUser && lookupSource !== "phone");
  const isEmailLocked = Boolean(existingUser && lookupSource !== "email");
  const isNameLocked = Boolean(existingUser);
  const showPhoneField = !existingUser || Boolean(formValues.phone);
  const showEmailField = !existingUser || Boolean(formValues.email);
  const showNameField = !existingUser || Boolean(formValues.name);
  const canSubmit = isAddUserFormValid(formValues, {
    existingUserId: existingUser?.id,
    isMappedToOutlet,
    lookupStatus,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || isSubmitting) return;

    setIsSubmitting(true);
    setSubmissionError("");

    try {
      await createUserAndMapToOutlet({
        outletId,
        userId: existingUser?.id || 0,
        phone: formValues.phone,
        email: formValues.email,
        name: formValues.name,
        role: formValues.role,
      });
      invalidateOutletUsersCache();
      navigate("/manage-users/add/success", {
        replace: true,
        state: { added: true },
      });
    } catch (error) {
      if (isDuplicateUserMappingError(error)) {
        setShowExistingUserDialog(true);
      } else {
        setSubmissionError(getUserMappingErrorMessage(error));
      }
      setIsSubmitting(false);
    }
  };

  const phoneHasError =
    touchedFields.phone &&
    Boolean(formValues.phone) &&
    !isValidPhoneNumber(formValues.phone);
  const emailHasError =
    touchedFields.email &&
    Boolean(formValues.email.trim()) &&
    !isValidEmail(formValues.email);

  return (
    <Page>
      <PageShell>
        <Header>
          <ActionButton
            type="button"
            aria-label="Go back"
            onClick={() => navigate("/manage-users", { replace: true })}
          >
            <img src="/logos/back_logo.svg" alt="" />
          </ActionButton>
          <HeaderTitle>Add new user</HeaderTitle>
        </Header>

        <Content>
          <SectionTitle>User details</SectionTitle>

          <AddUserForm id="add-user-form" onSubmit={handleSubmit}>
            <FormCard>
              {showPhoneField && (
                <div>
                  <TextInput
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    aria-label="Phone number"
                    aria-invalid={phoneHasError}
                    placeholder="Phone number"
                    value={formValues.phone}
                    readOnly={isPhoneLocked}
                    $readOnly={isPhoneLocked}
                    onChange={handleContactChange("phone")}
                    onBlur={handleContactBlur("phone")}
                    maxLength={10}
                  />
                  {phoneHasError && (
                    <FieldMessage>Enter exactly 10 digits.</FieldMessage>
                  )}
                </div>
              )}

              {showEmailField && (
                <div>
                  <TextInput
                    type="email"
                    autoComplete="email"
                    aria-label="Email"
                    aria-invalid={emailHasError}
                    placeholder="Email"
                    value={formValues.email}
                    readOnly={isEmailLocked}
                    $readOnly={isEmailLocked}
                    onChange={handleContactChange("email")}
                    onBlur={handleContactBlur("email")}
                  />
                  {emailHasError && (
                    <FieldMessage>Enter a valid email address.</FieldMessage>
                  )}
                </div>
              )}

              {showNameField && (
                <TextInput
                  type="text"
                  autoComplete="name"
                  aria-label="Full name"
                  placeholder="Full name"
                  value={formValues.name}
                  readOnly={isNameLocked}
                  $readOnly={isNameLocked}
                  onChange={updateField("name")}
                  maxLength={120}
                />
              )}

              <RoleDropdown
                id="add-user-role"
                value={formValues.role}
                placeholder="Role"
                disabled={isMappedToOutlet}
                onChange={(role) => {
                  setFormValues((currentValues) => ({
                    ...currentValues,
                    role,
                  }));
                  setSubmissionError("");
                }}
              />
            </FormCard>

            {lookupStatus === "checking" && (
              <LookupMessage role="status" aria-live="polite">
                Checking whether this user already exists…
              </LookupMessage>
            )}

            {existingUser && isMappedToOutlet && (
              <LookupMessage
                role="status"
                aria-live="polite"
                $isWarning
              >
                This user is already added to this outlet.
              </LookupMessage>
            )}

            {lookupError && (
              <LookupMessage role="alert" $isError>
                {lookupError}
              </LookupMessage>
            )}

            {submissionError && (
              <LookupMessage role="alert" $isError>
                {submissionError}
              </LookupMessage>
            )}
          </AddUserForm>
        </Content>

        <SubmitArea>
          <SubmitButton
            type="submit"
            form="add-user-form"
            aria-busy={isSubmitting}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? "Adding user" : "Add user"}
          </SubmitButton>
        </SubmitArea>
      </PageShell>

      {showExistingUserDialog && (
        <UserAlreadyExistsDialog
          isMappedToOutlet={isMappedToOutlet}
          onClose={() => setShowExistingUserDialog(false)}
        />
      )}
    </Page>
  );
};

export default AddUser;
